/**
 * Whether this is the desktop shell, and what it needs from the layout.
 *
 * ## Why the page has to know at all
 *
 * Because the header becomes the title bar. In the shell there is no browser
 * chrome above it, so it is the strip the window is dragged by - and the
 * system's own window controls are drawn over one end of it. Neither of those
 * is knowable from the web, so the shell says so through its preload.
 *
 * Everything here degrades to "no" in a browser, which is where almost all of
 * this app runs.
 */

interface DesktopBridge {
  desktop: true;
  platform: NodeJS.Platform | string;
  /** Room the system's own window controls need, in CSS pixels. */
  titlebar: { left: number; right: number };
}

declare global {
  interface Window {
    getvibed?: DesktopBridge;
  }
}

const bridge = typeof window === "undefined" ? undefined : window.getvibed;

/** Running inside the desktop shell rather than a browser tab. */
export const isDesktop = bridge?.desktop === true;

/**
 * The padding the header owes the window controls.
 *
 * macOS puts its traffic lights at the left, Windows and Linux put theirs at
 * the right, and a single fixed value is wrong on two platforms out of three -
 * either a gap nobody asked for or a logo underneath a close button.
 */
export const titlebarInset = bridge?.titlebar ?? { left: 0, right: 0 };

/**
 * Make an element a place the window can be dragged by.
 *
 * ## And why almost everything inside it needs the opposite
 *
 * A drag region swallows clicks. Every button, link and input inside one has to
 * opt back out or it stops working - silently, still looking exactly like a
 * button. That is the whole trap of a custom title bar, so the two helpers are
 * named as a pair and the `no-drag` one is used far more often.
 */
export const drag = isDesktop ? "app-drag" : "";
export const noDrag = isDesktop ? "app-no-drag" : "";
